import { Box, Typography } from '@mui/material';
import React from 'react';

import { UCS2, campaignSmsEstimate } from './smsSegments';

const CHAR_NAMES = {
    '\u00a0': 'non-breaking space',
    '\u202f': 'narrow non-breaking space',
    '\u200b': 'zero-width space',
    '\u200c': 'zero-width non-joiner',
    '\ufeff': 'zero-width no-break space',
    '\t': 'tab',
};

const describeChar = (c) => CHAR_NAMES[c] || c;

const MAX_LISTED_CHARS = 8;

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * Live SMS length readout for a campaign body: characters, segments and
 * encoding as Twilio counts them (``smsSegments.js``), including the opt-out
 * line the send appends. ``sms`` is the ``sms`` block of ``/filter_options/``
 * — absent on an older backend, in which case the opt-out line is left out of
 * the count and the MMS note uses the current 2-segment rule.
 */
const SmsSegmentCounter = ({ text, sms }) => {
    const optOutSuffix = sms?.opt_out_suffix || '';
    const mmsAbove = sms?.mms_above_segments ?? 2;
    const est = React.useMemo(
        () => campaignSmsEstimate(text, optOutSuffix),
        [text, optOutSuffix],
    );
    if (!(text || '').trim()) return null;

    const optOutLength = optOutSuffix ? campaignSmsEstimate('', optOutSuffix).characters : 0;
    const unicode = est.encoding === UCS2;
    const overMms = est.segments > mmsAbove;

    let summary = `SMS: ${plural(est.characters, 'character')} · ${plural(est.segments, 'segment')}`
        + ` · ${unicode ? 'Unicode (70 per segment)' : 'GSM-7 (160 per segment)'}`;
    if (optOutLength) {
        summary += sms?.opt_out_first_message_only
            ? ` — includes the ${optOutLength}-character opt-out line, which only a guest's first SMS carries.`
            : ` — includes the ${optOutLength}-character opt-out line added to every SMS.`;
    } else {
        summary += '.';
    }

    const notes = [];
    if (est.variables > 0) {
        notes.push(
            `Plus ${plural(est.variables, 'variable')}: their length varies per guest, `
            + "so a guest's message can run longer than this count.",
        );
    }
    if (est.hasTemplateLogic) {
        notes.push('Text in every {% if %} branch is counted, as if all of it were sent.');
    }
    if (est.converted.length > 0) {
        // The send swaps these (Twilio Smart Encoding); say so, or the count
        // looks like it ignored the curly quotes the operator typed.
        const listed = est.converted.slice(0, MAX_LISTED_CHARS).map(describeChar).join('  ');
        notes.push(`Sent as plain characters to keep the SMS short: ${listed}`);
    }

    const warnings = [];
    if (unicode) {
        const listed = est.nonGsmChars.slice(0, MAX_LISTED_CHARS).map(describeChar).join('  ');
        const more = est.nonGsmChars.length > MAX_LISTED_CHARS
            ? ` and ${est.nonGsmChars.length - MAX_LISTED_CHARS} more`
            : '';
        warnings.push(
            `Unicode because of: ${listed}${more}. That cuts each segment to 70 characters (67 when split).`,
        );
    }
    if (overMms) {
        warnings.push(
            `Over ${plural(mmsAbove, 'segment')}: US/Canada numbers get it as one MMS; `
            + `other countries are billed for all ${est.segments} segments.`,
        );
    }

    return (
        <Box sx={{ mt: 0.5, mx: 1.75 }} data-testid="sms-segment-counter">
            <Typography variant="caption" component="p" color="text.secondary">
                {summary}
            </Typography>
            {notes.map((n) => (
                <Typography key={n} variant="caption" component="p" color="text.secondary">
                    {n}
                </Typography>
            ))}
            {warnings.map((w) => (
                <Typography key={w} variant="caption" component="p" color="warning.main">
                    {w}
                </Typography>
            ))}
        </Box>
    );
};

export default SmsSegmentCounter;
